import {
  ApplicationConfig,
  DEFAULT_CURRENCY_CODE,
  LOCALE_ID,
  importProvidersFrom,
  inject,
  provideAppInitializer,
  provideBrowserGlobalErrorListeners,
  provideZonelessChangeDetection,
} from '@angular/core';
import { registerLocaleData } from '@angular/common';
import localeEn from '@angular/common/locales/en';
import localeIt from '@angular/common/locales/it';
import { provideRouter, withComponentInputBinding } from '@angular/router';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { HttpClient, provideHttpClient } from '@angular/common/http';
import { providePrimeNG } from 'primeng/config';
import { DialogService } from 'primeng/dynamicdialog';
import { ConfirmationService } from 'primeng/api';
import { MessageService } from 'primeng/api';
import { TranslateModule, TranslateLoader, TranslateService } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';

import { routes } from './app.routes';
import { theme } from './theme/primeng-config';
import { LanguageService, readPersistedLanguage } from './services/language.service';
import { primeNgTranslations } from './i18n/primeng-translations';

// Locale data for the languages we support, so Angular date/currency/decimal/
// number pipes format with the right separators and month/day names. Must be
// registered before LOCALE_ID resolves.
registerLocaleData(localeEn);
registerLocaleData(localeIt);

// Factory function for TranslateHttpLoader
export function createTranslateLoader(http: HttpClient) {
  return new TranslateHttpLoader(http, './assets/i18n/', '.json');
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideZonelessChangeDetection(),
    provideRouter(routes, withComponentInputBinding()),
    provideAnimationsAsync(),
    provideHttpClient(),
    importProvidersFrom(
      TranslateModule.forRoot({
        defaultLanguage: 'en',
        loader: {
          provide: TranslateLoader,
          useFactory: createTranslateLoader,
          deps: [HttpClient],
        },
      })
    ),
    provideAppInitializer(async () => inject(LanguageService).initializeLanguage()),
    // LOCALE_ID is static per injector: it is resolved once at bootstrap from the
    // persisted language (same 'app-language' value LanguageService uses). A
    // runtime language switch therefore reloads the app (see LanguageService) so
    // this factory re-runs and the pipes re-localize. EUR is our default currency.
    { provide: LOCALE_ID, useFactory: readPersistedLanguage },
    { provide: DEFAULT_CURRENCY_CODE, useValue: 'EUR' },
    providePrimeNG({ theme, translation: primeNgTranslations[readPersistedLanguage()] }),
    // PrimeNG services available app-wide
    DialogService,
    ConfirmationService,
    MessageService,
  ],
};

